import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, IconButton, Button, CircularProgress, Tooltip,
} from '@mui/material';
import { DeleteSweep, DragIndicator, LibraryAdd, RemoveCircleOutline, Shuffle } from '@mui/icons-material';
import { TrackArt, TrackPlayButton, TRACK_ACTION_ICON_SX } from './sonosTrackUi';
import SoundBars from './SoundBars';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import {
  SonosSpeaker,
  isSonosPlaying,
  getQueue, getCachedQueue, setCachedQueue, reapplyQueueImages,
  queueRemove, queueClear, queueReorder, queueShuffle, queuePlayAt, invalidateQueueCache,
  notifyQueueReordered,
  normalizeSpotifyTrackUri,
  type SonosQueueItem,
} from '../../services/sonosApi';
import { eventBus } from '../../services/EventBus';
import { mergeSonosSpeakers } from '../../services/sonosState';
import RepeatModeButton from './RepeatModeButton';
import SonosConfirmDialog from './SonosConfirmDialog';
import AddToPlaylistDialog from './AddToPlaylistDialog';
import { RepeatMode } from './sonosPlayMode';
import { SonosMessageHandler } from './sonosFeedback';
import { SONOS_BLUE, SURFACE, SURFACE_BORDER, sonosIconTapSx, sonosIconTapDangerSx, sonosTextButtonTapSx } from './sonosUiStyles';

interface QueueItem extends SonosQueueItem {}

function normTrackText(value?: string | null): string {
  return (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function isQueueTrackNowPlaying(
  item: QueueItem,
  coordinator: SonosSpeaker | null | undefined,
): boolean {
  if (!coordinator || !isSonosPlaying(coordinator.playback_state)) return false;
  const qt = normTrackText(item.title);
  const ct = normTrackText(coordinator.current_title);
  if (!qt || !ct || qt !== ct) return false;
  const qa = normTrackText(item.artist);
  const ca = normTrackText(coordinator.current_artist);
  if (qa && ca && qa !== ca) return false;
  return true;
}

const QUEUE_TRACK_INDICATOR_SX = {
  width: 36,
  height: 36,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  ml: 0.5,
} as const;

const QueueTrackTitle: React.FC<{
  title: string;
  fontSize: number;
  showBars: boolean;
  canPlay: boolean;
  onPlay?: () => void;
  playBusy?: boolean;
  showAddToPlaylist?: boolean;
  canAddToPlaylist?: boolean;
  onAddToPlaylist?: () => void;
  addToPlaylistLabel?: string;
}> = ({
  title, fontSize, showBars, canPlay, onPlay, playBusy,
  showAddToPlaylist, canAddToPlaylist, onAddToPlaylist, addToPlaylistLabel,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, width: '100%' }}>
    <Typography sx={{
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: '#fff',
      fontSize,
      fontWeight: 600,
    }}>
      {title}
    </Typography>
    {showAddToPlaylist && (
      <Box onPointerDown={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
        <Tooltip title={addToPlaylistLabel ?? ''}>
          <span>
            <IconButton
              size="small"
              onClick={() => onAddToPlaylist?.()}
              disabled={!canAddToPlaylist}
              sx={{ color: '#aaa', ...TRACK_ACTION_ICON_SX }}
            >
              <LibraryAdd sx={{ fontSize: 22 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    )}
    <Box
      sx={QUEUE_TRACK_INDICATOR_SX}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {showBars ? (
        <SoundBars
          active
          height={16}
          sx={{ mr: 0, width: 22 }}
        />
      ) : (
        <TrackPlayButton
          disabled={!canPlay || playBusy}
          onClick={() => onPlay?.()}
        />
      )}
    </Box>
  </Box>
);

interface SortableRowProps {
  id: string;
  item: QueueItem;
  index: number;
  variant: 'inline' | 'sheet';
  canControl: boolean;
  coordinator?: SonosSpeaker | null;
  onRemove: (index: number) => void;
  onPlayAt: (index: number) => void;
  onAddToPlaylist?: (uri: string) => void;
  playBusy?: boolean;
}

const SortableRow: React.FC<SortableRowProps> = ({
  id, item, index, variant, canControl, coordinator, onRemove, onPlayAt, onAddToPlaylist, playBusy,
}) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const isSheet = variant === 'sheet';
  const showPlayingBars = isQueueTrackNowPlaying(item, coordinator);
  const titleLabel = item.title || `Track ${index + 1}`;
  const spotifyUri = normalizeSpotifyTrackUri(item.uri);

  if (isSheet) {
    return (
      <Box
        ref={setNodeRef}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          opacity: isDragging ? 0.55 : 1,
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          py: 0.75,
          borderBottom: `1px solid ${SURFACE_BORDER}`,
          touchAction: 'pan-y',
        }}
      >
        <IconButton
          size="small"
          disabled={!canControl}
          onClick={() => onRemove(index)}
          disableRipple
          sx={{ color: '#e53935', p: 0.25, flexShrink: 0, ...sonosIconTapDangerSx }}
        >
          <RemoveCircleOutline sx={{ fontSize: 22 }} />
        </IconButton>
        <TrackArt url={item.image_url} size={40} />
        <Box sx={{ flex: 1, minWidth: 0, pr: 0.25 }}>
          <QueueTrackTitle
            title={titleLabel}
            fontSize={13}
            showBars={showPlayingBars}
            canPlay={canControl}
            onPlay={() => onPlayAt(index)}
            playBusy={playBusy}
            showAddToPlaylist
            canAddToPlaylist={canControl && Boolean(spotifyUri)}
            onAddToPlaylist={() => {
              if (spotifyUri) onAddToPlaylist?.(spotifyUri);
            }}
            addToPlaylistLabel={t('musicSystem.search.addToPlaylist')}
          />
          <Typography sx={{
            color: '#999',
            fontSize: 11,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {item.artist || ''}
          </Typography>
        </Box>
        <IconButton
          size="small"
          disabled={!canControl}
          disableRipple
          sx={{ color: '#888', p: 0.25, flexShrink: 0, cursor: 'grab', touchAction: 'none', ...sonosIconTapSx }}
          {...attributes}
          {...listeners}
        >
          <DragIndicator sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.55 : 1,
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 1,
        px: 1,
        mb: 0.5,
        background: SURFACE,
        border: `1px solid ${SURFACE_BORDER}`,
        borderRadius: '12px',
      }}
    >
      <IconButton
        size="small"
        disableRipple
        sx={{ color: '#666', cursor: 'grab', p: 0.25, flexShrink: 0, touchAction: 'none', ...sonosIconTapSx }}
        {...attributes}
        {...listeners}
      >
        <DragIndicator sx={{ fontSize: 18 }} />
      </IconButton>
      <TrackArt url={item.image_url} size={36} />
      <Box sx={{ flex: 1, minWidth: 0, pr: 0.25 }}>
        <QueueTrackTitle
          title={titleLabel}
          fontSize={12}
          showBars={showPlayingBars}
          canPlay={canControl}
          onPlay={() => onPlayAt(index)}
          playBusy={playBusy}
        />
        <Typography sx={{
          color: '#999',
          fontSize: 10,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {item.artist || ''}
        </Typography>
      </Box>
      <IconButton
        size="small"
        disabled={!canControl}
        onClick={() => onRemove(index)}
        disableRipple
        sx={{ color: '#e53935', p: 0.25, flexShrink: 0, ...sonosIconTapDangerSx }}
      >
        <RemoveCircleOutline sx={{ fontSize: 20 }} />
      </IconButton>
    </Box>
  );
};

interface SonosQueuePanelProps {
  speakerId: number | null;
  canControl?: boolean;
  shuffle?: boolean;
  onShuffleChange?: (value: boolean) => void;
  repeat?: RepeatMode;
  onRepeatChange?: (mode: RepeatMode) => void;
  variant?: 'inline' | 'sheet';
  /** Al abrir el overlay de cola, fuerza recarga desde API (evita caché sin portadas). */
  active?: boolean;
  coordinator?: SonosSpeaker | null;
  onPlayError?: SonosMessageHandler;
}

const SonosQueuePanel: React.FC<SonosQueuePanelProps> = ({
  speakerId,
  canControl = true,
  shuffle: shuffleProp,
  onShuffleChange,
  repeat: repeatProp,
  onRepeatChange,
  variant = 'inline',
  active = true,
  coordinator = null,
  onPlayError,
}) => {
  const { t } = useTranslation();
  const speakerIdRef = React.useRef(speakerId);
  speakerIdRef.current = speakerId;
  const initialCached = speakerId ? getCachedQueue(speakerId) : null;
  const [items, setItems] = useState<QueueItem[]>(initialCached ?? []);
  const [shuffleInternal, setShuffleInternal] = useState(false);
  const [repeatInternal, setRepeatInternal] = useState<RepeatMode>('off');
  const shuffle = shuffleProp ?? shuffleInternal;
  const repeat = repeatProp ?? repeatInternal;
  const [loading, setLoading] = useState(Boolean(speakerId && initialCached === null));
  const [removePrompt, setRemovePrompt] = useState<{ index: number; title: string } | null>(null);
  const [playBusyIndex, setPlayBusyIndex] = useState<number | null>(null);
  const [addToPlaylistOpen, setAddToPlaylistOpen] = useState(false);
  const [addToPlaylistUri, setAddToPlaylistUri] = useState<string | null>(null);
  const isSheet = variant === 'sheet';

  const openAddToPlaylist = useCallback((uri: string) => {
    setAddToPlaylistUri(uri);
    setAddToPlaylistOpen(true);
  }, []);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!speakerId) {
      setItems([]);
      setLoading(false);
      return;
    }

    const cached = getCachedQueue(speakerId);
    if (cached !== null) {
      setItems(cached);
      setLoading(false);
    } else if (!opts?.silent) {
      setLoading(true);
    }

    const targetSpeakerId = speakerId;
    try {
      const q = await getQueue(targetSpeakerId);
      if (speakerIdRef.current !== targetSpeakerId) return;
      setItems(q);
    } catch {
      if (speakerIdRef.current !== targetSpeakerId) return;
      if (cached === null) setItems([]);
    } finally {
      if (speakerIdRef.current === targetSpeakerId) {
        setLoading(false);
      }
    }
  }, [speakerId]);

  useEffect(() => {
    if (!speakerId || !active) {
      if (!speakerId) {
        setItems([]);
        setLoading(false);
      }
      return;
    }
    if (variant === 'sheet') {
      // Mostrar caché al instante; refrescar en silencio (evita titileo loading↔lista).
      const cached = getCachedQueue(speakerId);
      if (cached !== null) {
        setItems(cached);
        setLoading(false);
        invalidateQueueCache(speakerId);
        void load({ silent: true });
        return;
      }
      setLoading(true);
      void load();
      return;
    }
    const cached = getCachedQueue(speakerId);
    if (cached !== null) {
      setItems(cached);
      setLoading(false);
      void load({ silent: true });
      return;
    }
    void load();
  }, [speakerId, load, variant, active]);

  useEffect(() => {
    const onImagesUpdated = () => {
      if (!speakerIdRef.current) return;
      setItems((prev) => reapplyQueueImages(prev));
      const cached = getCachedQueue(speakerIdRef.current);
      if (cached) setCachedQueue(speakerIdRef.current, cached);
    };
    eventBus.on('sonos:queue-images-updated', onImagesUpdated);
    return () => {
      eventBus.off('sonos:queue-images-updated', onImagesUpdated);
    };
  }, []);

  // Aleatorio SHC: la cola se mezcló físicamente en el Sonos → recargar orden.
  useEffect(() => {
    const onReordered = (payload?: { speakerIds?: number[] }) => {
      const current = speakerIdRef.current;
      if (!current) return;
      const ids = payload?.speakerIds;
      if (Array.isArray(ids) && ids.length && !ids.includes(current)) return;
      invalidateQueueCache(current);
      void load({ silent: true });
    };
    eventBus.on('sonos:queue-reordered', onReordered);
    return () => {
      eventBus.off('sonos:queue-reordered', onReordered);
    };
  }, [load]);

  // Misma activación que PlaylistDetailSheet: scroll vertical gana; drag solo con hold.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 280, tolerance: 8 } }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!speakerId || !canControl) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((_, i) => `q-${i}` === active.id);
    const newIndex = items.findIndex((_, i) => `q-${i}` === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const prev = items;
    const next = arrayMove(items, oldIndex, newIndex);
    setItems(next);
    try {
      await queueReorder(speakerId, oldIndex, newIndex);
      setCachedQueue(speakerId, next);
    } catch {
      setItems(prev);
      void load({ silent: true });
    }
  };

  const handleRemove = async (index: number) => {
    if (!speakerId || !canControl) return;
    const prev = items;
    const next = items.filter((_, i) => i !== index);
    setItems(next);
    setCachedQueue(speakerId, next);
    try {
      await queueRemove(speakerId, index);
      eventBus.emit('sonos:queue-changed');
      void load({ silent: true });
    } catch {
      setItems(prev);
      setCachedQueue(speakerId, prev);
    }
  };

  const requestRemove = (index: number) => {
    const item = items[index];
    setRemovePrompt({
      index,
      title: item?.title?.trim() || `Track ${index + 1}`,
    });
  };

  const confirmRemove = () => {
    if (!removePrompt) return;
    const { index } = removePrompt;
    setRemovePrompt(null);
    void handleRemove(index);
  };

  const handleClear = async () => {
    if (!speakerId || !canControl) return;
    const prev = items;
    const prevCoordinator = coordinator;
    setItems([]);
    setCachedQueue(speakerId, []);
    // UI inmediata: sin tema, sin foto, sin barras (tarjetas + reproductor).
    if (prevCoordinator) {
      const cleared: SonosSpeaker = {
        ...prevCoordinator,
        playback_state: 'idle',
        current_title: null,
        current_artist: null,
        current_album: null,
        current_album_art_url: null,
        track_position: null,
        track_duration: null,
        playing_source_name: null,
      };
      mergeSonosSpeakers([cleared]);
    }
    eventBus.emit('sonos:queue-changed');
    try {
      const speakers = await queueClear(speakerId);
      if (speakers.length > 0) mergeSonosSpeakers(speakers);
      else if (prevCoordinator) {
        mergeSonosSpeakers([{
          ...prevCoordinator,
          playback_state: 'idle',
          current_title: null,
          current_artist: null,
          current_album: null,
          current_album_art_url: null,
          track_position: null,
          track_duration: null,
          playing_source_name: null,
        }]);
      }
      eventBus.emit('sonos:queue-changed');
      void load({ silent: true });
    } catch {
      setItems(prev);
      setCachedQueue(speakerId, prev);
      if (prevCoordinator) mergeSonosSpeakers([prevCoordinator]);
      eventBus.emit('sonos:queue-changed');
    }
  };

  const toggleShuffle = async () => {
    if (!speakerId || !canControl) return;
    const next = !shuffle;
    if (onShuffleChange) {
      onShuffleChange(next);
      return;
    }
    setShuffleInternal(next);
    try {
      const res = await queueShuffle(speakerId, next);
      if (res?.reordered) notifyQueueReordered([speakerId]);
    } catch {
      setShuffleInternal(!next);
    }
  };

  const handleRepeatChange = (mode: RepeatMode) => {
    if (!canControl) return;
    if (onRepeatChange) {
      onRepeatChange(mode);
      return;
    }
    setRepeatInternal(mode);
  };

  const handlePlayAt = async (index: number) => {
    if (!speakerId || !canControl || playBusyIndex != null) return;
    setPlayBusyIndex(index);
    try {
      const speakers = await queuePlayAt(speakerId, index);
      if (speakers.length > 0) mergeSonosSpeakers(speakers);
      eventBus.emit('sonos:queue-changed');
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      onPlayError?.(typeof detail === 'string' ? detail : t('musicSystem.playback.playError'), 'error');
    } finally {
      setPlayBusyIndex(null);
    }
  };

  if (!speakerId) {
    return (
      <Typography sx={{ color: '#999', fontSize: 12, p: 1, textAlign: isSheet ? 'center' : 'left' }}>
        {t('musicSystem.playback.selectSpeaker')}
      </Typography>
    );
  }

  if (loading && items.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} sx={{ color: SONOS_BLUE }} />
      </Box>
    );
  }

  const ids = items.map((_, i) => `q-${i}`);

  return (
    <Box>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 1,
        px: isSheet ? 0.5 : 0,
        minHeight: 40,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: isSheet ? 0.25 : 0 }}>
          <IconButton
            onClick={() => { void toggleShuffle(); }}
            disabled={!canControl}
            disableRipple
            aria-label={t('musicSystem.queue.shuffle')}
            sx={{
              color: shuffle ? SONOS_BLUE : '#aaa',
              p: 0.75,
              ...(shuffle ? {
                bgcolor: 'rgba(35,145,255,0.12)',
                '&:active': { bgcolor: 'rgba(35,145,255,0.28)', transform: 'scale(0.93)' },
              } : sonosIconTapSx),
            }}
          >
            <Shuffle />
          </IconButton>
          <RepeatModeButton
            mode={repeat}
            disabled={!canControl}
            onChange={handleRepeatChange}
          />
        </Box>
        <Button
          size="small"
          startIcon={<DeleteSweep sx={{ fontSize: 18 }} />}
          disabled={!canControl || items.length === 0}
          onClick={handleClear}
          disableRipple
          sx={{
            color: '#e57373',
            textTransform: 'none',
            fontSize: 12,
            fontWeight: 600,
            mr: isSheet ? 0.25 : 0,
            ...sonosTextButtonTapSx,
            '&:active': { bgcolor: 'rgba(229,57,53,0.14)', color: '#ff8a80' },
          }}
        >
          {t('musicSystem.queue.clear')}
        </Button>
      </Box>
      {items.length === 0 ? (
        <Typography sx={{ color: '#999', fontSize: 12, textAlign: isSheet ? 'center' : 'left', py: 2 }}>
          {t('musicSystem.queue.empty')}
        </Typography>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            {items.map((item, i) => (
              <SortableRow
                key={ids[i]}
                id={ids[i]}
                item={item}
                index={i}
                variant={variant}
                canControl={canControl}
                coordinator={coordinator}
                onRemove={requestRemove}
                onPlayAt={(idx) => { void handlePlayAt(idx); }}
                onAddToPlaylist={isSheet ? openAddToPlaylist : undefined}
                playBusy={playBusyIndex === i}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
      <SonosConfirmDialog
        open={Boolean(removePrompt)}
        historyKey="sonos-queue-remove-track"
        title={t('musicSystem.queue.removeConfirmTitle')}
        message={t('musicSystem.queue.removeConfirmBody', { name: removePrompt?.title ?? '' })}
        onCancel={() => setRemovePrompt(null)}
        onConfirm={confirmRemove}
      />
      <AddToPlaylistDialog
        open={addToPlaylistOpen}
        trackUri={addToPlaylistUri}
        onClose={() => { setAddToPlaylistOpen(false); setAddToPlaylistUri(null); }}
        onAdded={() => onPlayError?.(t('musicSystem.search.addedToPlaylist'), 'success')}
        onError={(msg) => onPlayError?.(msg, 'error')}
      />
    </Box>
  );
};

export default SonosQueuePanel;
